/**
 * Batch Runtime Factory — `@assistant-ui/react` 의 `useExternalStoreRuntime` 을
 * localStorage + stateless batch fetch 로 wrapping 한 hook.
 *
 * 절대 제약(plan):
 *  - DB / 서버 thread store / `assistant-cloud` 사용 금지 — 모든 thread state 는
 *    클라이언트 localStorage 에만 존재
 *  - 백엔드 호출은 stateless: 매 사용자 메시지마다 호스트 fetcher 가 전체 history 를
 *    받아 단일 응답을 돌려준다 (스트림 X, 우선 batch)
 *
 * 호스트가 주입할 책임:
 *  - `storageKey`: localStorage 키 (예: `pk.workbenchChat.v1`)
 *  - `fetcher`: (history) → Promise<ChatMessage[]> — assistant 메시지(들) 반환
 *
 * 본 factory 가 제공:
 *  - 메시지 read/write → localStorage debounce(200ms) 자동
 *  - onNew(user message) → fetcher 호출 → 응답을 thread 에 추가
 *  - isRunning 플래그 노출 (UI 가 로딩 표시)
 *  - resetThread(): 호스트가 "대화 새로고침" 버튼에 바인딩
 */
import { useExternalStoreRuntime } from "@assistant-ui/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ChatMessage } from "../types.ts"
import { appendReplies, buildErrorMessage, pickInitialMessages } from "./batch-helpers.ts"
import { clearMessages, debounce, saveMessages } from "./persist.ts"

/**
 * Batch runtime 설정 — 호스트 페이지가 자기 fetcher 와 함께 주입.
 */
export interface BatchRuntimeConfig {
	/** localStorage 저장 키 — 페이지/도메인별 분리 (예: "pk.workbenchChat.v1"). */
	readonly storageKey: string
	/**
	 * 사용자 메시지가 추가된 직후 호출되는 fetcher.
	 * @param history 사용자 메시지 포함 전체 thread history (시간순)
	 * @param signal AbortController.signal — 사용자가 cancel 하면 발화
	 * @returns assistant 응답 메시지 배열 (1개 이상). 빈 배열 반환 시 thread 변화 없음
	 */
	readonly fetcher: (
		history: ReadonlyArray<ChatMessage>,
		signal: AbortSignal
	) => Promise<ReadonlyArray<ChatMessage>>
	/** 처음 thread 가 비어 있을 때 주입할 초기 메시지 (예: 시스템 인사). 기본은 빈 배열. */
	readonly initialMessages?: ReadonlyArray<ChatMessage> | undefined
}

/**
 * Batch runtime hook — 컴포넌트에서 호출하면 `runtime` + 헬퍼들을 돌려준다.
 *
 * 사용 예:
 * ```tsx
 * const { runtime, resetThread, isRunning } = useBatchRuntime({
 *   storageKey: "pk.workbenchChat.v1",
 *   fetcher: async (history, signal) => {
 *     const r = await fetch("/admin/.../chat", { method: "POST", body: JSON.stringify({ history }), signal })
 *     return [{ role: "assistant", content: await r.text() }]
 *   }
 * })
 * return <ChatThread runtime={runtime} />
 * ```
 *
 * @param config storageKey + fetcher + 초기 메시지
 * @returns runtime (AssistantRuntime), resetThread (대화 새로고침), isRunning (로딩 플래그)
 */
export function useBatchRuntime(config: BatchRuntimeConfig): {
	readonly runtime: ReturnType<typeof useExternalStoreRuntime>
	readonly resetThread: () => void
	readonly isRunning: boolean
} {
	// 초기 messages — localStorage 가 비어 있으면 initialMessages 로 시작
	const [messages, setMessages] = useState<ReadonlyArray<ChatMessage>>(() =>
		pickInitialMessages(config.storageKey, config.initialMessages)
	)
	const [isRunning, setIsRunning] = useState(false)
	const abortRef = useRef<AbortController | undefined>(undefined)

	// debounced save — 200ms (메시지 추가 폭주 시 매번 직렬화 방지)
	const debouncedSave = useMemo(
		() =>
			debounce((next: ReadonlyArray<ChatMessage>) => {
				saveMessages(config.storageKey, next)
			}, 200),
		[config.storageKey]
	)

	// messages 변경 시 localStorage 동기화
	useEffect(() => {
		debouncedSave(messages)
		return () => debouncedSave.cancel()
	}, [messages, debouncedSave])

	const resetThread = useCallback(() => {
		abortRef.current?.abort()
		abortRef.current = undefined
		setIsRunning(false)
		clearMessages(config.storageKey)
		setMessages(config.initialMessages ?? [])
	}, [config.storageKey, config.initialMessages])

	const onNew = useCallback(
		async (message: { role: "user" | "assistant" | "system"; content: ReadonlyArray<unknown> }) => {
			// AppendMessage → ChatMessage 로 변환 (assistant-ui 의 AppendMessage 형태가
			// ThreadMessageLike 와 호환되므로 그대로 push)
			const userMsg = message as ChatMessage
			const nextHistory: ReadonlyArray<ChatMessage> = [...messages, userMsg]
			setMessages(nextHistory)

			const ctrl = new AbortController()
			abortRef.current = ctrl
			setIsRunning(true)
			try {
				const replies = await config.fetcher(nextHistory, ctrl.signal)
				if (ctrl.signal.aborted) return
				if (replies.length > 0) {
					setMessages((prev) => appendReplies(prev, replies))
				}
			} catch (err) {
				if (ctrl.signal.aborted) return
				// fetcher 에러는 assistant 메시지로 노출 — UI 가 사용자에게 표시 가능
				const errMsg = buildErrorMessage(err)
				setMessages((prev) => appendReplies(prev, [errMsg]))
			} finally {
				if (abortRef.current === ctrl) abortRef.current = undefined
				setIsRunning(false)
			}
		},
		[messages, config.fetcher]
	)

	const onCancel = useCallback(async () => {
		abortRef.current?.abort()
		abortRef.current = undefined
		setIsRunning(false)
	}, [])

	const runtime = useExternalStoreRuntime({
		messages,
		isRunning,
		setMessages: (next) => setMessages([...next] as ReadonlyArray<ChatMessage>),
		onNew,
		onCancel,
		convertMessage: (m) => m as ChatMessage
	})

	return { runtime, resetThread, isRunning }
}
